import type { JevTickBody, JevTickSummary, JevVerdict } from "./types";

const fixed = (value: number | null | undefined) =>
  value == null ? "?" : value.toFixed(2);

/** brian `jev_tick_replay` drop rules; the thresholds come with each verdict.
 * A body folded into another by a `same_body` pair carries the pair as
 * `mergedInto`. */
export const verdictText = (
  verdict: JevVerdict,
  mergedInto?: string | null,
): string => {
  const { rule, value, threshold } = verdict;
  switch (rule) {
    case "presence":
      return `presence ${fixed(value)} < ${fixed(threshold)}`;
    case "invalid":
      return "box has no area inside the picture";
    case "capped":
      return "over the board count (lowest presence of its kind)";
    case "duplicate":
      if (mergedInto)
        return `merged into ${mergedInto} (same body p ≥ ${fixed(threshold)})`;
      return "repeats a box an earlier step kept";
    default:
      return "kept";
  }
};

export const isDropped = (verdict: JevVerdict | null | undefined): boolean =>
  verdict?.rule != null;

export const verdictTone = (dropped: boolean): string =>
  dropped
    ? "bg-tint-declined text-ink-declined"
    : "bg-tint-recorded text-ink-recorded";

interface TickStep {
  label: string;
  value: string;
}

const count = (board: Record<string, string>, key: string) =>
  board[key] === "3_or_more" ? "3+" : (board[key] ?? "?");

const kept = (bodies: JevTickBody[]) => bodies.filter((b) => b.mark != null);

/** The tick's pipeline, one step per stage that ran. */
export const tickSteps = (tick: JevTickSummary): TickStep[] => {
  const asked = new Set(tick.calls.map((c) => c.question));
  const steps: TickStep[] = [];
  if (asked.has("board"))
    steps.push({
      label: "Board",
      value: `${count(tick.board, "person_count")}P · ${count(tick.board, "animal_count")}A`,
    });
  const marks = kept(tick.bodies).sort((a, b) => (a.mark ?? 0) - (b.mark ?? 0));
  if (asked.has("roster_diff"))
    steps.push({
      label: "Diff",
      value: marks.map((b) => b.roster ?? "?").join(" · ") || "—",
    });
  return steps;
};
